import React, { useState, useMemo, useEffect } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Edit, AlertCircle, RotateCcw, Search, List, Table2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import { useDeviceBrowserPolicies, type BlueJayRules } from "@/hooks/useDeviceBrowserPolicies";
import { useBlueJayRulesRead } from "@/hooks/useBlueJayRulesRead";
import { DEFAULT_BLUEJAY_CONFIG } from "@/config/nsfwRulePresets";
import { CategoryAccordion } from "@/components/CategoryAccordion";
import { ALL_SUBCATEGORY_NAMES } from "@/config/categoryHierarchy";

interface DeviceBrowserCategoriesTableProps {
  deviceId: string;
}

type ViewMode = 'accordion' | 'table';

export const DeviceBrowserCategoriesTable = ({
  deviceId
}: DeviceBrowserCategoriesTableProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [optimisticCategories, setOptimisticCategories] = useState<string[] | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('accordion');
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const { categories } = useWebsiteCategories();
  
  // Use the hook that updates blueJayRules
  const { updateCategories } = useDeviceBrowserPolicies(deviceId);
  
  // Read current blueJayRules from HTTP endpoint
  const { blueJayRules, isLoading: isLoadingRules, refetch } = useBlueJayRulesRead(deviceId);

  // Refetch data on mount to ensure fresh data
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Get device details
  const deviceData = useConvexQuery(api.devices.get.details, deviceId ? {
    device: deviceId as any
  } : "skip");

  // Get all levels to look up level browser policies
  const allLevelsResult = useConvexQuery(api.levels.list.all, { paginationOpts: { numItems: 100, cursor: null } });
  const allLevels = allLevelsResult?.page ?? [];

  // Helper to get current rules for mutation calls
  const getCurrentRules = (): BlueJayRules => {
    if (blueJayRules && typeof blueJayRules === 'object') {
      return {
        categories: blueJayRules.categories || [],
        allowlist: blueJayRules.allowlist || [],
        blocklist: blueJayRules.blocklist || [],
        config: {
          nsfwLevel: blueJayRules.config?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel,
          blockVideos: blueJayRules.config?.blockVideos ?? false,
          human: blueJayRules.config?.human ?? DEFAULT_BLUEJAY_CONFIG.human,
          accuracy: blueJayRules.config?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy,
        },
      };
    }
    return {
      categories: [],
      allowlist: [],
      blocklist: [],
      config: { ...DEFAULT_BLUEJAY_CONFIG, blockVideos: false },
    };
  };

  // Get blocked categories from blueJayRules (use optimistic if set)
  const blockedCategories = useMemo(() => {
    if (optimisticCategories !== null) {
      return optimisticCategories;
    }
    if (blueJayRules && typeof blueJayRules === 'object') {
      return blueJayRules.categories || [];
    }
    return [];
  }, [blueJayRules, optimisticCategories]);

  // Find the level this device belongs to and get its browser policy
  const levelBrowserPolicy = useMemo(() => {
    const levelName = (deviceData as any)?.level || (deviceData as any)?.device?.level;
    if (!levelName || allLevels.length === 0) return null;
    
    const level = allLevels.find((l: any) => l.name === levelName);
    
    if (!level) return null;
    
    // Look for blueJayRules.categories
    const bjRules = level.blueJayRules;
    if (bjRules && typeof bjRules === 'object') {
      if (Array.isArray(bjRules.categories)) {
        return { blockedCategories: bjRules.categories };
      }
    }
    
    return { blockedCategories: [] };
  }, [deviceData, allLevels]);

  const isLoading = deviceData === undefined || isLoadingRules || allLevelsResult === undefined;

  // Calculate statistics
  const stats = useMemo(() => {
    const total = ALL_SUBCATEGORY_NAMES.length;
    const blocked = blockedCategories.length;
    const allowed = total - blocked;
    return { total, blocked, allowed };
  }, [blockedCategories]);

  // Get the status of a category - now uses blueJayRules.categories array
  // If category name is in the array, it's blocked
  const getCategoryStatus = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    if (!category) return 'allowed';

    // Check if category is in the blocked list
    if (blockedCategories.includes(category.name)) {
      return 'blocked';
    }
    
    return 'allowed';
  };

  // Get the source - for device-level overrides, we show "Device"
  // Since we're now managing at device level directly, it's always "Device"
  const getCategorySource = (categoryId: string): string => {
    return 'Device';
  };

  const getRowBackgroundColor = (status: string) => {
    if (status === 'allowed') {
      return 'bg-green-50 hover:bg-green-100';
    } else if (status === 'blocked') {
      return 'bg-red-50 hover:bg-red-100';
    }
    return 'hover:bg-gray-50';
  };

  const handleStatusChangeLocal = (categoryId: string, newStatus: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [categoryId]: newStatus
    }));
  };

  const handleSaveChanges = async () => {
    try {
      console.log('=== SAVE CHANGES DEBUG ===');
      console.log('Pending changes:', pendingChanges);
      console.log('Current blocked categories:', blockedCategories);
      
      // Start with current blocked categories
      const newBlockedCategories = new Set(blockedCategories);

      // Apply pending changes
      Object.entries(pendingChanges).forEach(([categoryId, newStatus]) => {
        const category = categories.find(cat => cat.id === categoryId);
        
        if (category) {
          if (newStatus === 'blocked') {
            newBlockedCategories.add(category.name);
          } else {
            newBlockedCategories.delete(category.name);
          }
        }
      });

      const finalCategories = Array.from(newBlockedCategories);
      console.log('Final blocked categories to send:', finalCategories);

      // Set optimistic update immediately
      setOptimisticCategories(finalCategories);

      // Update using blueJayRules.categories, passing current rules and refetch callback
      await updateCategories(finalCategories, getCurrentRules(), refetch);
      
      // Clear pending changes and exit edit mode
      setPendingChanges({});
      setIsEditMode(false);
      setOptimisticCategories(null);
      
      toast({
        title: "Changes Saved",
        description: `${Object.keys(pendingChanges).length} category changes have been saved.`
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      setOptimisticCategories(null);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCancelChanges = () => {
    setPendingChanges({});
    setOptimisticCategories(null);
    setIsEditMode(false);
  };

  const handleRevertToDefaults = async () => {
    try {
      // Get level defaults
      const levelCategories = levelBrowserPolicy?.blockedCategories || [];
      
      setOptimisticCategories(levelCategories);
      
      await updateCategories(levelCategories, getCurrentRules(), refetch);
      
      setOptimisticCategories(null);
      setIsEditMode(false);
      toast({
        title: "Reverted to Defaults",
        description: "Category settings have been reset to level defaults."
      });
    } catch (error) {
      console.error('Error reverting to defaults:', error);
      setOptimisticCategories(null);
      toast({
        title: "Error",
        description: "Failed to revert to defaults. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleAllowAll = async () => {
    try {
      // Allow all = empty categories array (nothing blocked)
      setOptimisticCategories([]);
      
      await updateCategories([], getCurrentRules(), refetch);
      
      setOptimisticCategories(null);
      
      toast({
        title: "All Categories Allowed",
        description: "All website categories have been allowed for this device."
      });
    } catch (error) {
      console.error('Error allowing all categories:', error);
      setOptimisticCategories(null);
      toast({
        title: "Error",
        description: "Failed to allow all categories. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleBlockAll = async () => {
    try {
      // Block all = all category names from hierarchy
      setOptimisticCategories([...ALL_SUBCATEGORY_NAMES]);
      
      await updateCategories([...ALL_SUBCATEGORY_NAMES], getCurrentRules(), refetch);
      
      setOptimisticCategories(null);
      
      toast({
        title: "All Categories Blocked",
        description: "All website categories have been blocked for this device."
      });
    } catch (error) {
      console.error('Error blocking all categories:', error);
      setOptimisticCategories(null);
      toast({
        title: "Error",
        description: "Failed to block all categories. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Handler for accordion view changes
  const handleAccordionCategoriesChange = async (newBlockedCategories: string[]) => {
    try {
      setOptimisticCategories(newBlockedCategories);
      await updateCategories(newBlockedCategories, getCurrentRules(), refetch);
      setOptimisticCategories(null);
      toast({
        title: "Changes Saved",
        description: "Category settings have been updated."
      });
    } catch (error) {
      console.error('Error updating categories:', error);
      setOptimisticCategories(null);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Filter categories for table view
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(cat => cat.name.toLowerCase().includes(query));
  }, [categories, searchQuery]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span>Loading website categories...</span>
        </CardContent>
      </Card>
    );
  }

  if (!deviceData?.device?.deviceConfig) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-amber-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Device Configuration</h3>
          <p className="text-gray-600 text-center">
            This device has no configuration. Please configure the device to see website categories.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Statistics */}
      <div className="flex items-center gap-4 text-sm">
        <span>
          <span className="font-medium text-green-600">{stats.allowed}</span>
          <span className="text-muted-foreground ml-1">Allowed</span>
        </span>
        <span>
          <span className="font-medium text-red-600">{stats.blocked}</span>
          <span className="text-muted-foreground ml-1">Blocked</span>
        </span>
        <span>
          <span className="font-medium">{stats.total}</span>
          <span className="text-muted-foreground ml-1">Total</span>
        </span>
      </div>

      {/* Search and View Toggle */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={viewMode === 'accordion' ? 'default' : 'outline'}
            onClick={() => setViewMode('accordion')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'outline'}
            onClick={() => setViewMode('table')}
          >
            <Table2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-start">
        <div className="flex gap-2">
          {isEditMode ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancelChanges}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSaveChanges} disabled={Object.keys(pendingChanges).length === 0}>
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={handleAllowAll}>
                Allow All
              </Button>
              <Button variant="outline" size="sm" onClick={handleBlockAll}>
                Block All
              </Button>
              {deviceData?.level && (
                <Button variant="outline" size="sm" onClick={handleRevertToDefaults}>
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Revert to level defaults
                </Button>
              )}
              {viewMode === 'table' && (
                <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Accordion View */}
      {viewMode === 'accordion' && (
        <CategoryAccordion
          blockedCategories={blockedCategories}
          onCategoriesChange={handleAccordionCategoriesChange}
          searchQuery={searchQuery}
        />
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-0">Category</TableHead>
                  <TableHead className="w-20 sm:w-32">Status</TableHead>
                  {deviceData?.level && <TableHead className="hidden sm:table-cell w-20">Source</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCategories.map(category => {
                  const status = getCategoryStatus(category.id);
                  const source = getCategorySource(category.id);
                  return (
                    <TableRow key={category.id} className={`${getRowBackgroundColor(status)} animate-fade-in`}>
                      <TableCell className="min-w-0">
                        <div className="font-medium text-sm truncate">{category.name}</div>
                        <div className="text-xs text-gray-500 sm:hidden">
                          {deviceData?.level && `${source} • `}{status}
                        </div>
                      </TableCell>
                      <TableCell className="w-20 sm:w-32">
                        {isEditMode ? (
                          <Select 
                            value={pendingChanges[category.id] || getCategoryStatus(category.id)} 
                            onValueChange={value => handleStatusChangeLocal(category.id, value)}
                          >
                            <SelectTrigger className="w-full text-xs sm:text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-50">
                              <SelectItem value="allowed">
                                <div className="flex items-center justify-between w-full">
                                  <span>Allow</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="blocked">
                                <div className="flex items-center justify-between w-full">
                                  <span>Block</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Badge variant={status === 'allowed' ? 'default' : 'destructive'} className="text-xs">
                              {status === 'allowed' ? 'Allowed' : 'Blocked'}
                            </Badge>
                          </div>
                        )}
                      </TableCell>
                      {deviceData?.level && (
                        <TableCell className="hidden sm:table-cell w-20">
                          <span className="text-sm capitalize">{source}</span>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {isEditMode && viewMode === 'table' && (
        <div className="text-xs text-gray-500 p-4 bg-blue-50 rounded-lg">
          <p><strong>Edit Mode:</strong> Use the dropdowns to change category permissions for this device. 
          Click "Save Changes" to apply your changes or "Cancel" to discard them.</p>
        </div>
      )}
    </div>
  );
};
